import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiWelcomeData,
  type EnrollmentMultiWelcomeSubmitInput,
  type EnrollmentMultiWelcomeSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi Welcome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiWelcomeData>
 * @example const response = await getEnrollmentMultiWelcomeData()
 */
export const getEnrollmentMultiWelcomeData = async (context?: DefaultContext): Promise<EnrollmentMultiWelcomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiWelcomeData',
      query: `mutation getEnrollmentMultiWelcomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              userId
              firstName
              progress
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiWelcomeData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiWelcomeData = createQueryHook<undefined, EnrollmentMultiWelcomeData>(
  getEnrollmentMultiWelcomeData,
)

/**
 * Submit Enrollment Multi Welcome data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiWelcomeSubmitOutput>
 * @example const response = await submitEnrollmentMultiWelcomeData();
 */
export const submitEnrollmentMultiWelcomeData = async (
  enrollmentWelcomeSubmitInput: EnrollmentMultiWelcomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiWelcomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiWelcomeData',
      query: `mutation submitEnrollmentMultiWelcomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentWelcomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiWelcomeSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiWelcomeData = createMutationHook<
  EnrollmentMultiWelcomeSubmitInput,
  EnrollmentMultiWelcomeSubmitOutput
>(submitEnrollmentMultiWelcomeData)
